// Tutorial slide 2/5. Interactive mock chat bubble that cycles through the
// same hz → +pinyin → +translation reveal stages as the real ChatBubble.
// Teaches the small "reveal" affordance in the bottom-right of every AI
// message. User must reach at least the pinyin stage to advance.

import { useState } from 'react';
import { Eye, Languages, EyeOff, ChevronRight } from 'lucide-react';
import { C } from '@/styles/tokens';

type Stage = 'hidden' | 'pinyin' | 'full';

interface RevealDemoProps {
  onContinue: () => void;
}

const HZ = '你好！很高兴认识你';
const PY = 'Nǐ hǎo! Hěn gāoxìng rènshí nǐ';
const EN = 'Hi! Nice to meet you';

export function RevealDemo({ onContinue }: RevealDemoProps) {
  const [stage, setStage] = useState<Stage>('hidden');
  const touched = stage !== 'hidden';

  const next: Stage = stage === 'hidden' ? 'pinyin' : stage === 'pinyin' ? 'full' : 'hidden';
  const buttonLabel = stage === 'hidden' ? 'reveal' : stage === 'pinyin' ? 'translate' : 'hide';
  const ButtonIcon = stage === 'hidden' ? Eye : stage === 'pinyin' ? Languages : EyeOff;

  return (
    <div className="flex-1 flex flex-col">
      <div className="ff-display text-3xl mb-2" style={{ color: C.ink }}>
        Stuck on a message?
      </div>
      <div className="ff-body text-[14px] leading-relaxed mb-8" style={{ color: C.inkLight }}>
        Try reading the message in characters first. When you&apos;re ready, reveal the pinyin — then the translation.
      </div>

      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="w-full max-w-[320px]">
          <div className="ff-body text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: C.inkSofter }}>
            小美
          </div>
          <div
            className="rounded-2xl rounded-bl-sm px-4 py-3 ff-body text-sm anim-scale-in"
            style={{
              background: C.cream,
              color: C.ink,
              border: `1px solid ${C.mist}`,
              boxShadow: '0 1px 0 rgba(26,22,20,0.04), 0 8px 20px -10px rgba(120,90,70,0.10)',
            }}
          >
            <div className="ff-zh-serif text-[17px] leading-snug">{HZ}</div>
            {stage !== 'hidden' && (
              <div className="mt-1.5 anim-slide-up">
                <div className="ff-body text-[11px] opacity-80">{PY}</div>
                {stage === 'full' && <div className="ff-body text-[12px] opacity-90 italic">{EN}</div>}
              </div>
            )}
            <div className="mt-1 flex justify-end">
              <button
                type="button"
                onClick={() => setStage(next)}
                className="ff-body text-[10px] opacity-70 hover:opacity-100 inline-flex items-center gap-1 anim-pulse-glow rounded-full px-1.5"
                aria-label={
                  stage === 'hidden'
                    ? 'Reveal pinyin'
                    : stage === 'pinyin'
                      ? 'Show translation'
                      : 'Hide pinyin and translation'
                }
              >
                <ButtonIcon size={10} aria-hidden />
                {buttonLabel}
              </button>
            </div>
          </div>
          <div className="ff-body text-[11px] mt-3 italic text-center" style={{ color: C.inkSofter }}>
            {stage === 'hidden'
              ? 'Tap "reveal" on the bubble.'
              : stage === 'pinyin'
                ? 'Now tap "translate" for English.'
                : 'You can hide it all again with the same button.'}
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={onContinue}
        disabled={!touched}
        className="mt-6 py-3 rounded-full ff-body font-semibold text-white inline-flex items-center justify-center gap-2 disabled:opacity-40"
        style={{ background: C.vermillion }}
      >
        {touched ? 'Got it' : 'Try the reveal button first'}
        {touched && <ChevronRight size={16} aria-hidden />}
      </button>
    </div>
  );
}
